import { type JSX, useCallback, useEffect, useMemo, useState } from 'react';

import {
  arten as ladeArten,
  dateiHinterlegen,
  fassungen as ladeFassungen,
  mailplatzhalter as ladePlatzhalter,
  mailvorlagen as ladeMailvorlagen,
  neueFassung,
  probeversand as ladeProbeversand,
  probeversandSetzen,
  neueMailvorlage,
  wegeSetzen,
  type Fassung,
  type Mailplatzhalter,
  type Mailvorlage,
  type Nachzuholen,
  type Rechtstextart,
} from '../api/rechtstexte';
import { KUNDENARTNAME } from '../api/kunden';
import { alsTag, alsZeitpunkt } from '../datum';
import { ApiFehler } from '../api/fehler';
import { Dialog } from '../komponenten/Dialog';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';

/**
 * Rechtstexte pflegen (BUC-25).
 *
 * ============================================================================
 * ES GIBT KEIN „ÄNDERN" — NUR EIN „ABLÖSEN"
 * ============================================================================
 *
 * An jeder Fassung hängt die protokollierte Zustimmung eines Kunden: „Die
 * Zustimmung wird mit Zeitpunkt und Versionskennung hier protokolliert."
 * Wäre eine Fassung änderbar, ließe sich nachträglich bestimmen, wozu jemand
 * zugestimmt hat — und das Protokoll wäre wertlos.
 *
 * Die Maske bietet deshalb nur einen Weg: eine NEUE Fassung einstellen. Die
 * bisherige bekommt ihr Ablösedatum und bleibt auffindbar. Das ist kein
 * Umweg, sondern der Zweck.
 *
 * ============================================================================
 * WARUM DER PLATZHALTER SICHTBAR SEIN MUSS
 * ============================================================================
 *
 * Fünf Fassungen stehen als Platzhalter im Bestand und beschreiben, was
 * hineingehört, statt es vorzutäuschen. Der Versand weigert sich, sie
 * hinauszuschicken.
 *
 * Ein Platzhalter, den man für einen Rechtstext hält, ist gefährlicher als
 * gar kein Text: Beim fehlenden Text merkt es jemand. Deshalb steht die Marke
 * in der Zeile, nicht in einer Fußnote.
 *
 * ============================================================================
 * SEITENTYP EINSTELLUNGEN (ÄA-41)
 * ============================================================================
 *
 * Je Thema eine Karte: Probeversand, Nachzuholen, Versand je Textart,
 * Fassungen, Mailvorlagen. Die Warnungen (Probeversand an, Platzhalter
 * offen) stehen ÜBER den Karten — sie sind der Grund, warum noch nichts
 * hinausgeht, und keine Einstellung. Die Tabellen nehmen die ganze Reihe.
 */

/** Wer den Text bekommt — in Worten und nicht als Schlüssel. */
const WEGNAME: Readonly<Record<string, string>> = {
  anmelder: 'immer an den Anmelder',
  nach_kundenart: `${KUNDENARTNAME.geschaeft} an den Anmelder, ${KUNDENARTNAME.privat} an jeden Teilnehmer`,
};

type Entwurf = {
  readonly art: string;
  readonly stamm: string | null;
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly inhaltHtml: string;
  readonly gueltigAb: string;
  readonly platzhalter: boolean;
};

const LEER: Entwurf = {
  art: '',
  stamm: null,
  kennung: '',
  bezeichnung: '',
  inhaltHtml: '',
  gueltigAb: '',
  platzhalter: false,
};

export function Rechtstexte(): JSX.Element {
  const [arten, setzeArten] = useState<readonly Rechtstextart[]>([]);
  const [fassungen, setzeFassungen] = useState<readonly Fassung[]>([]);
  const [vorlagen, setzeVorlagen] = useState<readonly Mailvorlage[]>([]);
  const [platzhalter, setzePlatzhalter] = useState<readonly Mailplatzhalter[]>([]);
  const [vorlagenentwurf, setzeVorlagenentwurf] = useState<Mailvorlage | null>(null);
  const [probe, setzeProbe] = useState(false);
  const [nachzuholen, setzeNachzuholen] = useState<readonly Nachzuholen[]>([]);
  const [auchAbgeloeste, setzeAuchAbgeloeste] = useState(false);
  const [entwurf, setzeEntwurf] = useState<Entwurf | null>(null);
  const [gelesen, setzeGelesen] = useState<Fassung | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laedt, setzeLaedt] = useState(true);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const [a, f, v, p, pv] = await Promise.all([
        ladeArten(),
        ladeFassungen(auchAbgeloeste),
        ladeMailvorlagen(auchAbgeloeste),
        ladePlatzhalter('bedingungen'),
        ladeProbeversand(),
      ]);
      setzeArten(a);
      setzeFassungen(f);
      setzeVorlagen(v);
      setzePlatzhalter(p);
      setzeProbe(pv.erlaubt);
      setzeNachzuholen(pv.nachzuholen);
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [auchAbgeloeste]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const nachArt = useMemo(() => {
    const m = new Map<string, Rechtstextart>();
    for (const a of arten) m.set(a.schluessel, a);
    return m;
  }, [arten]);

  /*
   * WIE VIELE PLATZHALTER NOCH STEHEN.
   *
   * Die Zahl gehört nach oben und nicht ans Ende der Liste: Sie ist der
   * Grund, warum noch nichts hinausgeht, und wer die Maske öffnet, soll ihn
   * zuerst lesen.
   */
  const offenePlatzhalter = fassungen.filter(
    (f) => f.abgeloestAm === null && f.platzhalter,
  ).length;

  const ablösen = useCallback((f: Fassung) => {
    setzeMeldung(null);
    setzeFehler(null);
    setzeGelesen(null);
    setzeEntwurf({
      art: f.art,
      stamm: f.stamm,
      kennung: '',
      bezeichnung: f.bezeichnung,
      inhaltHtml: f.inhaltHtml,
      gueltigAb: '',
      platzhalter: false,
    });
  }, []);

  const probeSchalten = useCallback(async (erlaubt: boolean) => {
    setzeFehler(null);
    setzeMeldung(null);
    try {
      const { erlaubt: neu } = await probeversandSetzen(erlaubt);
      setzeProbe(neu);
      setzeMeldung(
        neu
          ? 'Der Probeversand ist eingeschaltet. Vor der Freigabe wieder ausschalten.'
          : 'Der Probeversand ist ausgeschaltet.',
      );
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, [holen]);

  /** Die Wege einer Textart setzen — was angehängt wird, was im Text steht. */
  const wegAendern = useCallback(
    async (art: Rechtstextart, feld: 'alsAnhang' | 'imMailtext', wert: boolean) => {
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const { arten: neu } = await wegeSetzen(art.schluessel, {
          alsAnhang: feld === 'alsAnhang' ? wert : art.alsAnhang,
          imMailtext: feld === 'imMailtext' ? wert : art.imMailtext,
          empfaengerweg: art.empfaengerweg,
        });
        setzeArten(neu);
        setzeMeldung('Gesichert.');
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      }
    },
    [],
  );

  const empfaengerwegAendern = useCallback(
    async (art: Rechtstextart, weg: 'anmelder' | 'nach_kundenart') => {
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const { arten: neu } = await wegeSetzen(art.schluessel, {
          alsAnhang: art.alsAnhang,
          imMailtext: art.imMailtext,
          empfaengerweg: weg,
        });
        setzeArten(neu);
        setzeMeldung('Gesichert.');
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      }
    },
    [],
  );

  const dateiWaehlen = useCallback(
    async (fassung: Fassung, datei: File | undefined) => {
      if (datei === undefined) return;
      setzeFehler(null);
      setzeMeldung(null);
      setzeLaedt(true);
      try {
        await dateiHinterlegen(fassung.id, datei);
        setzeMeldung(`${datei.name} ist hinterlegt und wird künftig angehängt.`);
        await holen();
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaedt(false);
      }
    },
    [holen],
  );

  const vorlageSichern = useCallback(async () => {
    if (vorlagenentwurf === null) return;
    setzeFehler(null);
    setzeMeldung(null);
    setzeLaedt(true);
    try {
      await neueMailvorlage({
        stamm: vorlagenentwurf.stamm,
        anlass: vorlagenentwurf.anlass,
        kundenart: vorlagenentwurf.kundenart,
        bezeichnung: vorlagenentwurf.bezeichnung,
        betreff: vorlagenentwurf.betreff,
        textRein: vorlagenentwurf.textRein,
        textHtml: vorlagenentwurf.textHtml,
      });
      setzeMeldung('Die Mailvorlage gilt; die bisherige ist abgelöst.');
      setzeVorlagenentwurf(null);
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [vorlagenentwurf, holen]);

  const speichern = useCallback(async () => {
    if (entwurf === null) return;
    setzeLaedt(true);
    setzeFehler(null);
    setzeMeldung(null);
    try {
      const e = await neueFassung({
        ...(entwurf.stamm === null ? {} : { stamm: entwurf.stamm }),
        art: entwurf.art,
        kennung: entwurf.kennung.trim(),
        bezeichnung: entwurf.bezeichnung.trim(),
        artikel: null,
        inhaltHtml: entwurf.inhaltHtml,
        ...(entwurf.gueltigAb === '' ? {} : { gueltigAb: entwurf.gueltigAb }),
        platzhalter: entwurf.platzhalter,
      });
      setzeMeldung(
        entwurf.stamm === null
          ? `Fassung ${String(e.version)} eingestellt.`
          : `Fassung ${String(e.version)} gilt; die bisherige ist abgelöst und bleibt auffindbar.`,
      );
      setzeEntwurf(null);
      await holen();
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, [entwurf, holen]);

  const vollstaendig =
    entwurf !== null
    && entwurf.art !== ''
    && entwurf.kennung.trim() !== ''
    && entwurf.bezeichnung.trim() !== ''
    && entwurf.inhaltHtml.trim() !== '';

  return (
    <div className="stapel">
      {/*
        * Kein eigenes <h1>: Den Titel setzt die Arbeitsflaeche in den Seitenkopf
        * (`setzeKopf`), und zwei Ueberschriften uebereinander waeren eine zu
        * viel.
        */}
      <p className="hilfe">
        AGB, Teilnahme-, Storno- und Widerrufsbedingungen mit Fassung und Versionskennung.
        Eine eingestellte Fassung wird nie geändert, sondern abgelöst — an ihr hängt
        die protokollierte Zustimmung der Kunden.
      </p>

      {/*
        * ========================================================
        * DER PROBEVERSAND (0077)
        * ========================================================
        *
        * Er steht GANZ OBEN und nicht unter „Weitere Einstellungen": Solange
        * er an ist, gehen unfertige Bedingungen an Kunden hinaus, und das
        * muss die erste Zeile sein, die jemand liest, der diese Maske öffnet.
        */}
      {probe && (
        <p className="warnung">
          <strong>Der Probeversand ist eingeschaltet.</strong> Platzhalterfassungen und
          Fassungen ohne Datei gehen an Kunden hinaus. Jede Nachricht trägt einen Hinweis und
          zählt <strong>nicht</strong> als Übermittlung{/* BUC-78 */} — wer sie erhält, hat keine
          Bedingungen erhalten, und die Widerrufsfrist läuft zwölf Monate und vierzehn Tage.
          Vor der Freigabe ausschalten.
        </p>
      )}

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && <p className="hinweiszeile">{meldung}</p>}

      {offenePlatzhalter > 0 && (
        <p className="warnung">
          {offenePlatzhalter === 1
            ? 'Eine Fassung ist noch ein Platzhalter'
            : `${String(offenePlatzhalter)} Fassungen sind noch Platzhalter`}{' '}
          und geht nicht an Kunden hinaus. Solange ein zustimmungspflichtiger Text fehlt, lässt
          sich eine Buchung nicht abschließen.
        </p>
      )}

      <Einstellungsseite>
        <Thema titel="Versand zur Probe" zusatz={probe ? 'eingeschaltet' : 'ausgeschaltet'}>
          <div className="feld">
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={probe}
                aria-label="Probeversand"
                onChange={(e) => {
                  void probeSchalten(e.target.checked);
                }}
              />
              Probeversand — unfertige Bedingungen dürfen hinausgehen
            </label>
          </div>
        </Thema>

        {nachzuholen.length > 0 && (
          <Thema titel={`Nachzuholen (${String(nachzuholen.length)})`}>
            <p className="hilfe">
              Diesen Empfängern ging nur eine Probe zu. Sobald die echten Fassungen stehen,
              müssen die Bedingungen noch einmal verschickt werden. Die Zeile
              verschwindet von selbst, sobald derselbe Empfänger dieselbe Textart echt bekommen
              hat.
            </p>
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle">
                <caption className="nur-vorlesen">Nachzuholende Übermittlungen</caption>
                <thead>
                  <tr>
                    <th>Buchung</th>
                    <th>Fassung</th>
                    <th>Empfänger</th>
                    <th>Als Probe am</th>
                  </tr>
                </thead>
                <tbody>
                  {nachzuholen.map((z) => (
                    <tr key={`${z.buchung}-${z.versionskennung}-${z.empfaenger}`}>
                      <td className="liste__nummer">{z.buchungsnummer}</td>
                      <td className="liste__nummer">{z.versionskennung}</td>
                      <td>{z.empfaenger}</td>
                      <td className="liste__einzeilig">{alsZeitpunkt(z.versandtAm)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Thema>
        )}

        {/*
          * ========================================================
          * WAS ANGEHAENGT WIRD UND WAS IN DER MAIL STEHT (BUC-78)
          * ========================================================
          *
          * Der Auftraggeber am 04.09.2026: „Es soll konfigurierbar sein, was in
          * der Mail steht und welche Dateien angehängt werden."
          *
          * Beide Haken zugleich sind zulässig — ein kurzer Text darf im Fenster
          * stehen UND als Datei mitgehen. Keiner von beiden ist es nicht: Eine
          * Art, die weder angehängt wird noch im Text steht, würde übermittelt,
          * ohne übermittelt zu werden.
          */}
        <Thema titel="Versand je Textart" zusatz="als Anhang, im Mailtext, an wen" breit>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">Wege je Textart</caption>
              <thead>
                <tr>
                  <th>Textart</th>
                  <th>als Anhang</th>
                  <th>im Mailtext</th>
                  <th>Empfänger</th>
                </tr>
              </thead>
              <tbody>
                {arten.map((a) => (
                  <tr key={a.schluessel}>
                    <td>
                      {a.bezeichnung}
                      {a.nurVerbraucher && (
                        <>
                          {' '}
                          {/* E-62 */}<span className="kanalliste__name">nur Verbraucher</span>
                        </>
                      )}
                    </td>
                    <td>
                      <label className="liste__schalter">
                        <input
                          type="checkbox"
                          checked={a.alsAnhang}
                          aria-label={`${a.bezeichnung} als Anhang`}
                          /* Der letzte Weg bleibt stehen — sonst ginge nichts hinaus. */
                          disabled={a.alsAnhang && !a.imMailtext}
                          onChange={(e) => {
                            void wegAendern(a, 'alsAnhang', e.target.checked);
                          }}
                        />
                      </label>
                    </td>
                    <td>
                      <label className="liste__schalter">
                        <input
                          type="checkbox"
                          checked={a.imMailtext}
                          aria-label={`${a.bezeichnung} im Mailtext`}
                          disabled={a.imMailtext && !a.alsAnhang}
                          onChange={(e) => {
                            void wegAendern(a, 'imMailtext', e.target.checked);
                          }}
                        />
                      </label>
                    </td>
                    <td>
                      <select
                        className="feld__eingabe"
                        value={a.empfaengerweg}
                        aria-label={`Empfänger für ${a.bezeichnung}`}
                        onChange={(e) => {
                          void empfaengerwegAendern(
                            a,
                            e.target.value as 'anmelder' | 'nach_kundenart',
                          );
                        }}
                      >
                        <option value="anmelder">immer an den Anmelder</option>
                        <option value="nach_kundenart">
                          Geschäftskunden an den Anmelder, Privatkunden an jeden Teilnehmer
                        </option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Thema>

        {/*
          * Der Schalter „Abgelöste mitzeigen" stand bis ÄA-41 in der
          * unsichtbaren Tabellenüberschrift — nur eine Vorlesehilfe fand ihn.
          * Jetzt steht er sichtbar rechts im Kopf der Karte.
          */}
        <Thema
          titel="Fassungen"
          zusatz={
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={auchAbgeloeste}
                onChange={(e) => {
                  setzeAuchAbgeloeste(e.target.checked);
                }}
              />
              Abgelöste mitzeigen
            </label>
          }
          breit
        >
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">Geltende Fassungen</caption>
              <thead>
                <tr>
                  <th>Art</th>
                  <th>Fassung</th>
                  <th>Versionskennung</th>
                  <th>Gilt ab</th>
                  <th>Empfänger</th>
                  <th>Zustimmung</th>
                  <th>Datei</th>
                  <th>Zustand</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {fassungen.map((f) => {
                  const art = nachArt.get(f.art);
                  return (
                    <tr key={f.id} className={f.abgeloestAm === null ? '' : 'liste__zeile--blass'}>
                      <td>
                        {art?.bezeichnung ?? f.art}
                        {f.artikelbezeichnung !== null && (
                          <>
                            {' '}
                            <span className="kanalliste__name">nur {f.artikelbezeichnung}</span>
                          </>
                        )}
                      </td>
                      <td className="liste__einzeilig">{f.version}</td>
                      <td className="liste__nummer">{f.kennung}</td>
                      <td className="liste__einzeilig">{alsTag(f.gueltigAb)}</td>
                      <td>
                        {WEGNAME[art?.empfaengerweg ?? 'anmelder']}
                        {art?.nurVerbraucher === true && (
                          <>
                            {' '}
                            <span className="kanalliste__name">nur Verbraucher</span>
                          </>
                        )}
                      </td>
                      <td>
                        {/*
                          * BUC-25: Zugestimmt wird den AGB und den Bedingungen. Eine
                          * Widerrufsbelehrung wird ZUR KENNTNIS GENOMMEN — sie ist
                          * eine Belehrung und keine Vereinbarung.
                          */}
                        {art?.zustimmungspflichtig === true ? 'erforderlich' : 'Kenntnisnahme'}
                      </td>
                      <td>
                        {/*
                          * BUC-78: Die Datei ist der Anhang. Ohne sie weigert sich
                          * der Versand — deshalb steht hier nicht „—", sondern der
                          * Weg dorthin.
                          */}
                        {f.dateiname !== null ? (
                          <span className="marke marke--gut">{f.dateiname}</span>
                        ) : f.abgeloestAm !== null ? (
                          <span className="hilfe">keine</span>
                        ) : (
                          <label className="knopf knopf--klein">
                            PDF hinterlegen
                            <input
                              type="file"
                              accept="application/pdf"
                              className="nur-vorlesen"
                              aria-label={`PDF für ${f.bezeichnung} hinterlegen`}
                              onChange={(e) => {
                                void dateiWaehlen(f, e.target.files?.[0]);
                              }}
                            />
                          </label>
                        )}
                      </td>
                      <td>
                        {f.abgeloestAm !== null ? (
                          <span className="marke">abgelöst</span>
                        ) : f.platzhalter ? (
                          <span className="marke marke--warn">Platzhalter</span>
                        ) : (
                          <span className="marke marke--gut">gilt</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => {
                            setzeGelesen(gelesen?.id === f.id ? null : f);
                          }}
                        >
                          {gelesen?.id === f.id ? 'schließen' : 'lesen'}
                        </button>
                        {f.abgeloestAm === null && (
                          <button
                            type="button"
                            className="knopf knopf--klein"
                            onClick={() => {
                              ablösen(f);
                            }}
                          >
                            neue Fassung
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {fassungen.length === 0 && !laedt && (
                  <tr>
                    <td colSpan={9}>Noch keine Fassung eingestellt.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {entwurf === null && (
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf"
                onClick={() => {
                  setzeMeldung(null);
                  setzeFehler(null);
                  setzeEntwurf(LEER);
                }}
              >
                Fassung einstellen
              </button>
            </div>
          )}
        </Thema>

        {/*
          * ========================================================
          * WAS IN DER MAIL STEHT (ANG-14, BUC-109)
          * ========================================================
          *
          * Der Nachrichtenkörper stand bis zum 04.09.2026 im Quelltext. Jetzt
          * ist er eine Vorlage mit Fassungen — wie alles andere, dessen Wortlaut
          * beim Kunden ankommt.
          */}
        <Thema titel="Mailvorlagen" zusatz="Wortlaut der Nachricht" breit>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle">
              <caption className="nur-vorlesen">Mailvorlagen</caption>
              <thead>
                <tr>
                  <th>Vorlage</th>
                  <th>Anlass</th>
                  <th>gilt für</th>
                  <th>Fassung</th>
                  <th>Betreff</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {vorlagen.map((v) => (
                  <tr key={v.id} className={v.abgeloestAm === null ? '' : 'liste__zeile--blass'}>
                    <td>{v.bezeichnung}</td>
                    <td>{v.anlass}</td>
                    <td>
                      {v.kundenart === null ? (
                        <span className="hilfe">alle Kundenarten</span>
                      ) : (
                        KUNDENARTNAME[v.kundenart as keyof typeof KUNDENARTNAME]
                      )}
                    </td>
                    <td className="liste__einzeilig">{v.version}</td>
                    <td>{v.betreff}</td>
                    <td>
                      {v.abgeloestAm === null && (
                        <button
                          type="button"
                          className="knopf knopf--klein"
                          onClick={() => {
                            setzeMeldung(null);
                            setzeFehler(null);
                            setzeVorlagenentwurf(v);
                          }}
                        >
                          neue Fassung
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {vorlagen.length === 0 && !laedt && (
                  <tr>
                    <td colSpan={6}>Noch keine Mailvorlage eingestellt.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Thema>
      </Einstellungsseite>

      {/*
        AUCH DAS LESEN STEHT IM FENSTER.

        Es ist dieselbe Bewegung wie beim Bearbeiten: Ein Knopf in der Zeile,
        und der Inhalt erscheint. Ließe man ihn als einziges unter der Tabelle
        stehen, hätte dieselbe Maske zwei Verhaltensweisen für dieselbe Geste.
      */}
      {gelesen !== null && (
        <Dialog
          titel={`${gelesen.bezeichnung} — Fassung ${String(gelesen.version)}`}
          unterzeile={gelesen.kennung}
          breite="breit"
          beiSchliessen={() => {
            setzeGelesen(null);
          }}
        >
          {gelesen.platzhalter && (
            <p className="warnung">
              Dies ist ein Platzhalter und geht nicht an Kunden hinaus.
            </p>
          )}
          {/*
            * Der Text wird als Text gezeigt und nicht als HTML gesetzt.
            *
            * Er stammt aus der Datenbank und wäre über `dangerouslySetInnerHTML`
            * ein Einfallstor: Wer eine Fassung einstellen darf, könnte damit
            * Skript in die Maske jedes Kollegen bringen (OWASP A03). Für die
            * Pflege genügt der Quelltext — der Kunde bekommt ohnehin das PDF.
            */}
          <pre className="quelltext">{gelesen.inhaltHtml}</pre>
        </Dialog>
      )}

      {entwurf !== null && (
        <Dialog
          titel={
            entwurf.stamm === null
              ? 'Neue Fassung einstellen'
              : 'Neue Fassung — die bisherige wird abgelöst'
          }
          breite="breit"
          beiSchliessen={() => {
            setzeEntwurf(null);
          }}
        >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void speichern();
          }}
        >
          <p className="feld__hilfe">
            Die bisherige Fassung bleibt stehen und auffindbar. Zustimmungen, die auf sie
            protokolliert wurden, bleiben auf sie bezogen.
          </p>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rtArt">
              Art <span className="feld__pflicht">Pflicht</span>
            </label>
            <select
              id="rtArt"
              className="feld__eingabe"
              value={entwurf.art}
              disabled={entwurf.stamm !== null}
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, art: e.target.value });
              }}
            >
              <option value="">— bitte wählen —</option>
              {arten.map((a) => (
                <option key={a.schluessel} value={a.schluessel}>
                  {a.bezeichnung}
                </option>
              ))}
            </select>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rtKennung">
              Versionskennung <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id="rtKennung"
              className="feld__eingabe"
              value={entwurf.kennung}
              placeholder="AGB 2026-01"
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, kennung: e.target.value });
              }}
            />
            <p className="feld__hilfe">
              Das ist die Angabe, die im Zustimmungsprotokoll steht und im Streitfall gelesen
              wird — nicht die laufende Nummer.
            </p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rtBezeichnung">
              Bezeichnung <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id="rtBezeichnung"
              className="feld__eingabe"
              value={entwurf.bezeichnung}
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, bezeichnung: e.target.value });
              }}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rtGueltigAb">
              Gilt ab
            </label>
            <input
              id="rtGueltigAb"
              type="date"
              className="feld__eingabe feld__eingabe--schmal"
              value={entwurf.gueltigAb}
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, gueltigAb: e.target.value });
              }}
            />
            <p className="feld__hilfe">Leer heißt: ab heute.</p>
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="rtInhalt">
              Text <span className="feld__pflicht">Pflicht</span>
            </label>
            <textarea
              id="rtInhalt"
              className="feld__eingabe"
              rows={14}
              value={entwurf.inhaltHtml}
              onChange={(e) => {
                setzeEntwurf({ ...entwurf, inhaltHtml: e.target.value });
              }}
            />
          </div>

          <div className="feld">
            <label className="liste__schalter">
              <input
                type="checkbox"
                checked={entwurf.platzhalter}
                onChange={(e) => {
                  setzeEntwurf({ ...entwurf, platzhalter: e.target.checked });
                }}
              />
              Noch ein Platzhalter — nicht an Kunden verschicken
            </label>
          </div>

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeEntwurf(null);
              }}
            >
              Abbrechen
            </button>
            <button type="submit" className="knopf" disabled={!vollstaendig || laedt}>
              Fassung einstellen
            </button>
          </div>
        </form>
        </Dialog>
      )}

      {vorlagenentwurf !== null && (
        <Dialog
          titel="Mailvorlage — neue Fassung"
          unterzeile={vorlagenentwurf.bezeichnung}
          breite="breit"
          beiSchliessen={() => {
            setzeVorlagenentwurf(null);
          }}
        >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void vorlageSichern();
          }}
        >
          <p className="feld__hilfe">
            Bekannte Platzhalter:{' '}
            {platzhalter.map((p) => `{{${p.schluessel}}} (${p.bezeichnung})`).join(', ')}. Ein
            unbekannter bliebe in der Nachricht stehen und ginge so an den Kunden — deshalb
            werden sie beim Einstellen geprüft.
          </p>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="mvBetreff">
              Betreff <span className="feld__pflicht">Pflicht</span>
            </label>
            <input
              id="mvBetreff"
              className="feld__eingabe"
              value={vorlagenentwurf.betreff}
              onChange={(e) => {
                setzeVorlagenentwurf({ ...vorlagenentwurf, betreff: e.target.value });
              }}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="mvHtml">
              Nachricht (HTML) <span className="feld__pflicht">Pflicht</span>
            </label>
            <textarea
              id="mvHtml"
              className="feld__eingabe"
              rows={10}
              value={vorlagenentwurf.textHtml}
              onChange={(e) => {
                setzeVorlagenentwurf({ ...vorlagenentwurf, textHtml: e.target.value });
              }}
            />
          </div>

          <div className="feld">
            <label className="feld__beschriftung" htmlFor="mvRein">
              Nachricht (Klartext) <span className="feld__pflicht">Pflicht</span>
            </label>
            <textarea
              id="mvRein"
              className="feld__eingabe"
              rows={8}
              value={vorlagenentwurf.textRein}
              onChange={(e) => {
                setzeVorlagenentwurf({ ...vorlagenentwurf, textRein: e.target.value });
              }}
            />
            <p className="feld__hilfe">
              Der Rückfall für Programme, die kein HTML zeigen. Ein automatisch abgeleiteter
              Klartext liest sich immer wie ein Unfall — deshalb steht er hier eigens.
            </p>
          </div>

          <div className="knopfreihe">
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setzeVorlagenentwurf(null);
              }}
            >
              Abbrechen
            </button>
            {/* Nicht „Fassung einstellen": Denselben Namen trägt der Knopf
                der Rechtstexte, und zwei gleich benannte Knöpfe auf einer
                Maske sind eine Verwechslung, die wartet. */}
            <button type="submit" className="knopf" disabled={laedt}>
              Mailvorlage einstellen
            </button>
          </div>
        </form>
        </Dialog>
      )}
    </div>
  );
}
